import React from "react";
import getAvatarUrl from "../../../utils/get-avatar-url";

// ─── Sub-components ───────────────────────────────────────────────────────────

function MemberAvatar({ name, avatarUrl }) {
  if (avatarUrl) {
    return <img src={avatarUrl} alt={name} className="mark-attendance-avatar-img" />;
  }
  return (
    <span className="mark-attendance-avatar">
      {name ? name.charAt(0).toUpperCase() : "?"}
    </span>
  );
}

function SortIcon({ field, sortField, sortDir }) {
  const active = sortField === field;
  return (
    <img
      src="/img/new-icons/arrows-up-down.svg"
      alt={active ? (sortDir === "asc" ? "sorted ascending" : "sorted descending") : "sort"}
      className={`mark-attendance-sort-icon${active ? ` mark-attendance-sort-icon--${sortDir}` : ""}`}
    />
  );
}

function StatusButtons({ status, onChange }) {
  return (
    <div className="mark-attendance-status-btns">
      <button
        type="button"
        className={`mark-attendance-status-btn mark-attendance-status-btn--attended${status === "attended" ? " mark-attendance-status-btn--active" : ""}`}
        onClick={() => onChange("attended")}
      >
        {status === "attended" && <img src="/img/svg/vuesax-linear-tick-square-scheduling.svg" alt="" />}
        Attended
      </button>
      <button
        type="button"
        className={`mark-attendance-status-btn mark-attendance-status-btn--absent${status === "absent" ? " mark-attendance-status-btn--active" : ""}`}
        onClick={() => onChange("absent")}
      >
        {status === "absent" && <img src="/img/svg/vuesax-linear-tick-square-scheduling-red.svg" alt="" />}
        Absent
      </button>
      <button
        type="button"
        className={`mark-attendance-status-btn mark-attendance-status-btn--pending${status === "pending" ? " mark-attendance-status-btn--active" : ""}`}
        onClick={() => onChange("pending")}
      >
        {status === "pending" && <img src="/img/svg/vuesax-linear-tick-square-scheduling-yellow.svg" alt="" />}
        Pending
      </button>
    </div>
  );
}

const KIND_LABELS = { class: "Classes", pt: "PT Session", facility: "Facility", blackout_summary: "Blocked Time" };
const EDIT_KIND_LABELS = { class: "Class", pt: "PT Session", facility: "Facility" };
const RECURRENCE_LABELS = { one_time: "One-time", daily: "Daily", weekly: "Weekly", monthly: "Monthly", custom: "Custom" };

function formatPanelDate(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "—";
  const day = date.getDate();
  const month = date.toLocaleDateString("en-US", { month: "short" });
  const year = date.getFullYear();
  const weekday = date.toLocaleDateString("en-US", { weekday: "short" });
  return `${month} ${day}, ${year} (${weekday})`;
}

function formatPanelTimeRange(start, end) {
  if (!(start instanceof Date)) return "—";
  const opts = { hour: "numeric", minute: "2-digit", hour12: true };
  const a = start.toLocaleTimeString("en-US", opts).toLowerCase().replace(" ", "");
  if (!(end instanceof Date) || Number.isNaN(end.getTime())) return a;
  const b = end.toLocaleTimeString("en-US", opts).toLowerCase().replace(" ", "");
  return `${a} - ${b}`;
}

function formatDurationMins(minutes) {
  if (minutes == null || Number.isNaN(Number(minutes))) return "—";
  const m = Number(minutes);
  if (m < 60) return `${m} mins`;
  const h = Math.floor(m / 60);
  const r = m % 60;
  return r ? `${h}h ${r}m` : `${h}h`;
}

function extractCoaches(raw, theme) {
  if (!raw) return [];
  const list = Array.isArray(raw.coaches) ? raw.coaches : [];
  const seen = new Set();
  const out = [];
  for (const entry of list) {
    const c = entry?.coachId ?? entry?.coach ?? entry;
    if (!c || typeof c !== "object") continue;
    const id = String(c._id ?? c.id ?? "");
    if (!id || seen.has(id)) continue;
    seen.add(id);
    out.push({
      id,
      name: String(c.name ?? "").trim() || "Coach",
      role: String(c.role ?? "").trim() || "Coach",
      avatarUrl: getAvatarUrl(c, false, theme, theme !== "dark"),
    });
  }
  return out;
}

function DetailRow({ icon, label, children }) {
  return (
    <div className="mark-attendance-detail-row">
      <span className="mark-attendance-detail-label">
        <img src={icon} alt="" aria-hidden />
        {label}
      </span>
      <span className="mark-attendance-detail-value">{children}</span>
    </div>
  );
}

function resolveAvatarUrl(raw, theme) {
  if (!raw) return getAvatarUrl({}, false, theme, theme !== "dark");
  if (typeof raw === "string") return raw;
  if (typeof raw === "object" && typeof raw.s3Path === "string" && raw.s3Path) {
    return getAvatarUrl({ avatar: raw }, false, theme, theme !== "dark");
  }
  return getAvatarUrl({}, false, theme, theme !== "dark");
}

function normalizeStatus(raw) {
  const s = String(raw ?? "").toLowerCase();
  if (s === "attended") return "attended";
  if (s === "absent" || s === "no_show") return "absent";
  return "pending";
}

function parseBookings(list, theme) {
  return list.map((b, i) => {
    const rawDate = b.start_datetime ?? null;
    let bookedAt = null;
    if (rawDate) {
      const d = new Date(rawDate);
      if (!Number.isNaN(d.getTime())) {
        bookedAt = d.toLocaleString("en-US", {
          hour: "numeric",
          minute: "2-digit",
          hour12: true,
          month: "short",
          day: "numeric",
          year: "numeric",
        });
      }
    }
    return {
      _key: i,
      id: String(b.booking_number ?? b._id ?? b.id ?? i),
      dbId: String(b._id ?? b.id ?? b.booking_number ?? i),
      name: String(b.member_name ?? "Member"),
      email: b.member_email ? String(b.member_email) : null,
      avatarUrl: resolveAvatarUrl(b.member_avatar, theme),
      userType: b.member_is_push ? "PUSH" : "PULL",
      bookedAt,
      status: normalizeStatus(b.status),
    };
  });
}

// ─── Main component ───────────────────────────────────────────────────────────

const KIND_ENDPOINT = { class: "classes", "pt-session": "pt-sessions", pt: "pt-sessions", facility: "facilities" };

const MarkAttendanceView = ({ event, onBack, onEdit, theme = "dark" }) => {
  const [activeTab, setActiveTab] = React.useState("members");
  const [statusFilter, setStatusFilter] = React.useState("all");
  const [sortField, setSortField] = React.useState(null);
  const [sortDir, setSortDir] = React.useState("asc");
  const [members, setMembers] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [eventDetail, setEventDetail] = React.useState(null);

  React.useEffect(() => {
    const rawId = event?.rawId;
    if (!rawId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);

    const params = new URLSearchParams({
      bookable_event_id: rawId,
      include: "member_type",
    });
    if (event.start instanceof Date && !Number.isNaN(event.start.getTime())) {
      params.set("date", event.start.toISOString().slice(0, 10));
    }

    fetch(`/actions/scheduling/coach/bookings?${params}`, {
      credentials: "same-origin",
      headers: { Accept: "application/json" },
    })
      .then(async (res) => {
        const data = await res.json().catch(() => []);
        return Array.isArray(data) ? data : [];
      })
      .then((list) => {
        if (cancelled) return;
        setMembers(parseBookings(list, theme));
      })
      .catch(() => {
        if (cancelled) return;
        setError("Could not load bookings.");
        setMembers([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [event?.rawId, event?.start]);

  React.useEffect(() => {
    const rawId = event?.rawId;
    const kind = event?.kind ?? event?.type;
    const endpoint = KIND_ENDPOINT[kind];
    if (!rawId || !endpoint) return;
    let cancelled = false;
    setEventDetail(null);
    fetch(`/actions/scheduling/${endpoint}`, {
      credentials: "same-origin",
      headers: { Accept: "application/json" },
    })
      .then((r) => r.json().catch(() => []))
      .then((list) => {
        if (cancelled) return;
        const arr = Array.isArray(list) ? list : [];
        const found = arr.find((item) => String(item._id ?? item.id) === String(rawId));
        if (found) setEventDetail(found);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [event?.rawId, event?.kind, event?.type]);

  const counts = React.useMemo(() => ({
    all: members.length,
    attended: members.filter((m) => m.status === "attended").length,
    absent: members.filter((m) => m.status === "absent").length,
    pending: members.filter((m) => m.status === "pending").length,
  }), [members]);

  const filteredMembers = React.useMemo(() => {
    let list = statusFilter === "all"
      ? members
      : members.filter((m) => m.status === statusFilter);
    if (sortField) {
      list = [...list].sort((a, b) => {
        const va = String(a[sortField] ?? "");
        const vb = String(b[sortField] ?? "");
        return sortDir === "asc" ? va.localeCompare(vb) : vb.localeCompare(va);
      });
    }
    return list;
  }, [members, statusFilter, sortField, sortDir]);

  function handleSort(field) {
    if (sortField === field) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDir("asc");
    }
  }

  function setMemberStatus(key, status) {
    setMembers((prev) => {
      if (status === "attended" || status === "absent") {
        const member = prev.find((m) => m._key === key);
        if (member) {
          fetch(`/actions/scheduling/bookings/${encodeURIComponent(member.dbId)}/attendance`, {
            method: "POST",
            credentials: "same-origin",
            headers: { "Content-Type": "application/json", Accept: "application/json" },
            body: JSON.stringify({ status }),
          }).catch(() => {});
        }
      }
      return prev.map((m) => (m._key === key ? { ...m, status } : m));
    });
  }

  const PILLS = [
    { key: "all",      label: "All" },
    { key: "attended", label: `Attended (${counts.attended})` },
    { key: "absent",   label: `Absent (${counts.absent})` },
    { key: "pending",  label: `Pending (${counts.pending})` },
  ];

  return (
    <div className="mark-attendance-view">
      <button type="button" className="mark-attendance-back" onClick={onBack}>
        <span className="mark-attendance-back-icon">
          <img src="/img/new-icons/arrow-square-left.svg" alt="" className="mark-attendance-back-arrow" />
        </span>
        Back
      </button>

      <div className="mark-attendance-header">
        <div className="mark-attendance-header-thumb">
          {event?.thumbnailUrl ? (
            <img src={event.thumbnailUrl} alt={event.title} className="mark-attendance-header-thumb-img" />
          ) : (
            <div className="mark-attendance-header-thumb-placeholder" />
          )}
        </div>
        <div className="mark-attendance-header-info">
          <h1 className="mark-attendance-header-title">{event?.title ?? "Class"}</h1>
          {event?.description && (
            <p className="mark-attendance-header-desc">{event.description}</p>
          )}
          <button type="button" className="mark-attendance-edit-btn" onClick={() => onEdit?.(event)}>
            <img src="/img/new-icons/edit-scheduling.svg" alt="" />
            {`Edit ${EDIT_KIND_LABELS[event?.kind ?? event?.type] ?? "Event"}`}
          </button>
        </div>
      </div>

      <div className="mark-attendance-card">
        <div className="mark-attendance-tabs">
          <button
            type="button"
            className="mark-attendance-tab"
            aria-current={activeTab === "members" ? "true" : undefined}
            onClick={() => setActiveTab("members")}
          >
            Members ({counts.all})
          </button>
          <button
            type="button"
            className="mark-attendance-tab"
            aria-current={activeTab === "details" ? "true" : undefined}
            onClick={() => setActiveTab("details")}
          >
            Details
          </button>
        </div>

        {activeTab === "members" && (
          <>
            <div className="mark-attendance-pills">
              {PILLS.map(({ key, label }) => (
                <button
                  key={key}
                  type="button"
                  className={`mark-attendance-pill${statusFilter === key ? " mark-attendance-pill--active" : ""}`}
                  onClick={() => setStatusFilter(key)}
                >
                  {label}
                </button>
              ))}
            </div>

            {loading && (
              <div className="mark-attendance-loading">Loading bookings…</div>
            )}

            {!loading && error && (
              <div className="mark-attendance-error">{error}</div>
            )}

            {!loading && !error && (
              <div className="mark-attendance-table-wrap">
                <table className="mark-attendance-table">
                  <thead>
                    <tr>
                      <th>Booking ID</th>
                      <th>
                        <button type="button" className="mark-attendance-sort-btn" onClick={() => handleSort("name")}>
                          Members Name
                          <SortIcon field="name" sortField={sortField} sortDir={sortDir} />
                        </button>
                      </th>
                      <th>User Type</th>
                      <th>
                        <button type="button" className="mark-attendance-sort-btn" onClick={() => handleSort("bookedAt")}>
                          Event Time
                          <SortIcon field="bookedAt" sortField={sortField} sortDir={sortDir} />
                        </button>
                      </th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredMembers.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="mark-attendance-empty">No bookings found.</td>
                      </tr>
                    ) : (
                      filteredMembers.map((m) => (
                        <tr key={m._key} className="mark-attendance-row">
                          <td className="mark-attendance-booking-id">{m.id}</td>
                          <td>
                            <div className="mark-attendance-member-cell">
                              <MemberAvatar name={m.name} avatarUrl={m.avatarUrl} />
                              <div>
                                <div className="mark-attendance-member-name">{m.name}</div>
                                {m.email && <div className="mark-attendance-member-email">{m.email}</div>}
                              </div>
                            </div>
                          </td>
                          <td className="mark-attendance-user-type">{m.userType}</td>
                          <td className="mark-attendance-booked-at">{m.bookedAt ?? "—"}</td>
                          <td>
                            <StatusButtons
                              status={m.status}
                              onChange={(s) => setMemberStatus(m._key, s)}
                            />
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {activeTab === "details" && (() => {
          const d = eventDetail ?? {};
          const evKind = event?.kind ?? event?.type ?? "";
          const kindKey = evKind === "pt-session" ? "pt" : evKind;
          const recurrence = RECURRENCE_LABELS[d.recurrence_type ?? d.schedule_type] ?? d.recurrence_type ?? d.schedule_type ?? event?.frequency ?? "—";
          const location = d.location?.toString().trim() || event?.location || "—";
          const durationMins = d.duration_minutes ?? null;
          const capacity = d.capacity ?? null;
          const coaches = extractCoaches(d, theme);
          return (
            <div className="mark-attendance-details">
              <DetailRow icon="/img/new-icons/type-scheduling.svg" label="Type">
                <span className={`mark-attendance-detail-type mark-attendance-detail-type--${kindKey}`}>
                  {KIND_LABELS[kindKey] ?? "Event"}
                </span>
              </DetailRow>
              <DetailRow icon="/img/new-icons/vuesax-linear-calendar-schedule.svg" label="Date">
                {formatPanelDate(event?.start)}
              </DetailRow>
              <DetailRow icon="/img/new-icons/vuesax-linear-clock.svg" label="Time">
                {formatPanelTimeRange(event?.start, event?.end)}
              </DetailRow>
              <DetailRow icon="/img/new-icons/duration-scheduling.svg" label="Duration">
                {formatDurationMins(durationMins)}
              </DetailRow>
              <DetailRow icon="/img/new-icons/reccurence-scheduling.svg" label="Recurrence">
                {recurrence}
              </DetailRow>
              <DetailRow icon="/img/new-icons/location-scheduling.svg" label="Location">
                {location}
              </DetailRow>
              {capacity != null && (
                <DetailRow icon="/img/new-icons/classes-icon.svg" label="Capacity">
                  {`${capacity} ${Number(capacity) === 1 ? "Seat" : "Seats"}`}
                </DetailRow>
              )}
              <DetailRow icon="/img/new-icons/attended-scheduling.svg" label="Attended">
                {`${counts.attended} ${counts.attended === 1 ? "Member" : "Members"}`}
              </DetailRow>
              <DetailRow icon="/img/new-icons/absent-scheduling.svg" label="Absent">
                {`${counts.absent} ${counts.absent === 1 ? "Member" : "Members"}`}
              </DetailRow>
              {coaches.length > 0 && (
                <div className="mark-attendance-detail-row mark-attendance-detail-row--coaches">
                  <span className="mark-attendance-detail-label">
                    <img src="/img/new-icons/coach-scheduling.svg" alt="" aria-hidden />
                    Coaches
                  </span>
                  <ul className="mark-attendance-detail-coaches">
                    {coaches.map((c) => (
                      <li key={c.id} className="mark-attendance-detail-coach">
                        <img src={c.avatarUrl} alt="" className="mark-attendance-detail-coach-avatar" />
                        <span className="mark-attendance-detail-coach-text">
                          <span className="mark-attendance-detail-coach-name">{c.name}</span>
                          <span className="mark-attendance-detail-coach-role">{c.role}</span>
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          );
        })()}
      </div>
    </div>
  );
};

export default MarkAttendanceView;
